import { useEffect, useRef, useState } from 'react';
import { BiErrorCircle } from 'react-icons/bi';
import { CgShoppingCart } from 'react-icons/cg';
import { MdOutlineArrowForwardIos } from 'react-icons/md';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BackgroundColor,
  BrighterGreen,
  ErrorRed,
  PrimaryTitleGray,
  DarkGreen,
} from '../../app/theme/tokens/colors';
import { PrimaryButton } from '../ui/Inputs';
import {
  ORDER_FLOW_STEPS,
  useLayoutStore,
  usePortalStore,
} from '../../stores/portalStore';
import { ContentErrorBoundary } from './errorBoundary';
import { MainHeader } from './header/header';
import { Menu } from './menu/menu';
import { SimplePopup } from '../overlays';
import { Alert, Slide, Snackbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useResponsive } from '../../hooks/useResponsive';
import { setSnackbar } from '../../stores/redux/reducers/snackbar';

const Disclaimer = () => {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '0px',
        left: '0px',
        right: '0px',
        height: '70px',
        zIndex: '2000',
        backgroundColor: 'white',
        boxSizing: 'border-box',
        border: `1px solid ${DarkGreen}`,
      }}
    >
      <p style={{ margin: '25px', fontWeight: 'bold' }}>
        Os dados utilizados pela companhia nesta plataforma são fictícios e meramente ilustrativos. A White Martins atua em conformidade com a LGPD, comprometida com a privacidade
        de clientes e parceiros.
      </p>
    </div>
  );
};

export const WideLayout = (props) => {
  const snackbarState = useSelector((state) => state.snackbar);
  const dispatch = useDispatch();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const isSmallScreen = isMobile || isTablet;

  const hideSnackbar = () => {
    dispatch(setSnackbar({ ...snackbarState, open: false }));
  };

  // Erro de renderização: mostra no popup de alerta e, ao confirmar, reinicia o conteúdo da tela.
  const [contentResetKey, setContentResetKey] = useState(0);
  const renderErrorPendingRef = useRef(false);
  const handleRenderError = (error) => {
    renderErrorPendingRef.current = true;
    setCurrentAlertError(
      `Ocorreu um erro ao exibir esta tela: ${String(error?.message ?? error)}`
    );
  };
  const closeAlertError = () => {
    setCurrentAlertError('');
    if (renderErrorPendingRef.current) {
      renderErrorPendingRef.current = false;
      setContentResetKey((key) => key + 1);
    }
  };

  const style = { backgroundColor: BackgroundColor, height: '100vh' };

  const [searchParams] = useSearchParams();
  const breadcrumb = usePortalStore((state) => state.breadcrumb);
  const setSelectedCategory = usePortalStore((state) => state.setSelectedCategory);
  const currentAlertError = usePortalStore((state) => state.currentAlertError);
  const setCurrentAlertError = usePortalStore(
    (state) => state.setCurrentAlertError
  );
  const showDisclaimer = false;
  const setBreadcrumb = usePortalStore((state) => state.setBreadcrumb);
  const ui = usePortalStore((state) => state.ui);
  const setMenuOpenState = usePortalStore((state) => state.setMenuOpenState);

  // ref para medir largura
  const contentRef = useRef(null);
  const { setContentWidth, setContentHeight } = useLayoutStore((state) => ({
    setContentWidth: state.setContentWidth,
    setContentHeight: state.setContentHeight,
  }));

  if (isDesktop) {
    style.width = '100%';
    style.margin = 'auto auto';
  }

  const location = useLocation();
  const navigate = useNavigate();

  const isOnOrderFlow = () => location.pathname.indexOf('/order/') === 0;
  const isOnAssistanceFlow = () => location.pathname.indexOf('/technicalAssistance/') === 0;
  const isOnCollectFlow = () => location.pathname.indexOf('/collectEquipments/') === 0;
  const isOnRechargeFlow = () => location.pathname.indexOf('/recharge/') === 0;
  const isOnSalesFlow = () => location.pathname.indexOf('/sales/') === 0;
  const isOnSuballocationFlow = () =>
    location.pathname.indexOf('/suballocation/') === 0;

  const shouldShowBreadcrumb = () =>
    isOnOrderFlow() ||
    isOnAssistanceFlow() ||
    isOnCollectFlow() ||
    isOnRechargeFlow() ||
    isOnSalesFlow() ||
    isOnSuballocationFlow();

  // lógica de breadcrumb
  useEffect(() => {
    if (!shouldShowBreadcrumb()) return;

    const path = location.pathname;

    if (path.endsWith('/sales/selectPatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT_SALES);
    } else if (path.endsWith('/sales/catalog')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SALES_CATALOG);
    } else if (path.endsWith('/sales/finish')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_SALES_ORDER);
    } else if (path.endsWith('/suballocation/selectPatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT_SUBALLOCATION);
    } else if (path.endsWith('/suballocation/catalog')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SUBALLOCATION_CATALOG);
    } else if (path.endsWith('/suballocation/finish')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_SUBALLOCATION_ORDER);
    } else if (path.endsWith('/newOrder')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_AREA);
    } else if (path.endsWith('/selectPatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT);
    } else if (path.endsWith('/selectCategory')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_CATEGORY);
    } else if (path.indexOf('/selectSubCategory') > 0) {
      const category = searchParams.get('category');
      const categoryId = searchParams.get('categoryId');
      setSelectedCategory(category, categoryId);
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_SUBCATEGORY);
    } else if (path.endsWith('/configureEquipmentMockup')) {
      setBreadcrumb(ORDER_FLOW_STEPS.CONFIGURE_EQUIPMENT_MOCKUP);
    } else if (path.endsWith('/configureEquipment')) {
      setBreadcrumb(ORDER_FLOW_STEPS.CONFIGURE_EQUIPMENT);
    } else if (path.endsWith('/order/finish')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_ORDER);
    } else if (path.endsWith('/assistanceToPatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT_ASSISTANCE);
    } else if (path.endsWith('/assistance')) {
      setBreadcrumb(ORDER_FLOW_STEPS.EQUIPMENT_ASSISTANCE);
    } else if (path.endsWith('/finishAssistanceOrder')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_ASSISTANCE_ORDER);
    } else if (path.endsWith('/collectFromPatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT_COLLECTION);
    } else if (path.endsWith('/collectEquipments')) {
      setBreadcrumb(ORDER_FLOW_STEPS.COLLECT_EQUIPMENT);
    } else if (path.endsWith('/finishCollectEquipments')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_COLLECT_ORDER);
    } else if (path.endsWith('/rechargePatient')) {
      setBreadcrumb(ORDER_FLOW_STEPS.SELECT_PATIENT_RECHARGE);
    } else if (path.endsWith('/finishRecharge')) {
      setBreadcrumb(ORDER_FLOW_STEPS.FINISH_RECHARGE_ORDER);
    }
  }, [location.pathname, searchParams, setBreadcrumb, setSelectedCategory]);

  useEffect(() => {
    const updateSize = () => {
      if (isDesktop && contentRef.current) {
        setContentHeight(contentRef.current.offsetHeight);
        setContentWidth(contentRef.current.offsetWidth);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [setContentHeight, setContentWidth, isDesktop, ui.menuOpenState, showDisclaimer, breadcrumb.length]);

  const hasBreadcrumb = shouldShowBreadcrumb();
  const desktopContentHeight = hasBreadcrumb ? 'calc(100% - 50px)' : '100%';
  let mobileContentHeight = '100%';
  if (hasBreadcrumb) {
    mobileContentHeight = showDisclaimer ? 'calc(100vh - 210px)' : 'calc(100% - 50px)';
  } else if (showDisclaimer) {
    mobileContentHeight = 'calc(100vh - 160px)';
  }

  return (
    <div style={style}>
      {showDisclaimer && <Disclaimer />}

      {currentAlertError !== '' && (
        <SimplePopup
          zIndex={10000}
          width={600}
          outlet={
            <div>
              <div className="error-message">
                <h3 className="erro-text">
                  <BiErrorCircle
                    style={{
                      color: ErrorRed,
                      fontWeight: '500',
                      fontSize: '32px',
                    }}
                  />
                  Atenção!
                </h3>
              </div>
              <div style={{ textAlign: 'center' }}>{currentAlertError}</div>
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <PrimaryButton onClick={closeAlertError}>Ok</PrimaryButton>
              </div>
            </div>
          }
        />
      )}

      <MainHeader />

      {/* Overlay quando menu está aberto em mobile/tablet */}
      {isSmallScreen && ui.menuOpenState && (
        <div
          onClick={() => setMenuOpenState(false)}
          style={{
            position: 'fixed',
            top: '60px',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2,
            cursor: 'pointer',
          }}
        />
      )}

      <div
        className="content-main"
        style={{
          display: 'block',
          overflow: 'hidden',
          position: 'relative',
          height: 'calc(100vh - 60px)',
        }}
      >
        <Menu />

        {/* LAYOUT DESKTOP - Menu fica fixo na lateral */}
        {isDesktop ? (
          <div
            ref={contentRef}
            className="content-stage"
            style={{
              marginLeft: ui.menuOpenState ? '200px' : '0px',
              width: ui.menuOpenState ? 'calc(100% - 200px)' : '100%',
              background: '#ececec',
              flex: 1,
              height: 'calc(100vh - 60px)',
              transition: 'margin-left 0.3s ease',
            }}
          >
            {/* Breadcrumb */}
            {shouldShowBreadcrumb() && (
              <div
                className="breadcrumb"
                style={{
                  width: '100%',
                  height: '50px',
                  padding: '10px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {breadcrumb.map((x, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                    {idx > 0 && (
                      <div
                        style={{
                          padding: '0 10px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <MdOutlineArrowForwardIos />
                      </div>
                    )}
                    {idx === 0 && <CgShoppingCart style={{ marginRight: 8, fontSize: '18px' }} />}
                    <div
                      onClick={() => navigate(x.link)}
                      style={{
                        cursor: 'pointer',
                        color: idx === breadcrumb.length - 1 ? BrighterGreen : PrimaryTitleGray,
                        fontWeight: idx === breadcrumb.length - 1 ? 'bold' : 'normal',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {x.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Conteúdo */}
            <div
              style={{
                width: '100%',
                overflow: 'auto',
                height: desktopContentHeight,
              }}
            >
              <ContentErrorBoundary
                key={`${location.pathname}-${contentResetKey}`}
                onError={handleRenderError}
              >
                {props.outlet || null}
              </ContentErrorBoundary>
            </div>
          </div>
        ) : (
          /* LAYOUT MOBILE/TABLET - Menu em overlay sobre o conteúdo */
          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {/* Breadcrumb */}
            {shouldShowBreadcrumb() && (
              <div
                style={{
                  height: '50px',
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {breadcrumb.map((x, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                    {idx > 0 && (
                      <div
                        style={{
                          padding: '0 10px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <MdOutlineArrowForwardIos />
                      </div>
                    )}
                    {idx === 0 && <CgShoppingCart style={{ marginRight: 8, fontSize: '18px' }} />}
                    <div
                      onClick={() => navigate(x.link)}
                      style={{
                        cursor: 'pointer',
                        color: idx === breadcrumb.length - 1 ? BrighterGreen : PrimaryTitleGray,
                        fontWeight: idx === breadcrumb.length - 1 ? 'bold' : 'normal',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {x.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Conteúdo sem marginLeft - Menu fica por cima */}
            <div
              style={{
                width: '100%',
                overflow: 'auto',
                height: mobileContentHeight,
              }}
            >
              <ContentErrorBoundary
                key={`${location.pathname}-${contentResetKey}`}
                onError={handleRenderError}
              >
                {props.outlet || null}
              </ContentErrorBoundary>
            </div>
          </div>
        )}
      </div>

      {/* Snackbar global */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={1000}
        TransitionComponent={Slide}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbarState.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
